// The loader (`/`, DESIGN §3.2, §3.4, §8 R1–R5, R15, §9): decides which build
// this device runs, stages and commits it, runs it inside the frame with
// window.mytheseus, watches it start, and draws the runtime's layer over it.
//
//   import { boot } from './boot.jsx';
//   boot(document.getElementById('app'));
//
// The decisions are pure (lib/launch.js); this file does what they say.
import { render } from 'preact';
import { env } from '../lib/env.js';
import { postJSON, errorCopy } from '../lib/api.js';
import { getLocal, setLocal, delLocal, KEYS, logUpdate } from '../lib/local.js';
import { kv } from '../lib/kv.js';
import {
  loadDevice, updateDevice, withBooting, withCommit, withReady, withFailure, withRetry, withAckSent,
  keptDigests, claimCode, announceSwitch, onSwitch, withHeartbeat, markerAlive, bootLock, withStaleMarker, claimBoot,
} from '../lib/device.js';
import { fetchApp, cachedApp, fetchState, cachedPage } from '../lib/session.js';
import { stage, pruneBuilds, withBuildCacheUse } from '../lib/stage.js';
import { Outbox, actionWrite, uuid } from '../lib/outbox.js';
import { Poller } from '../lib/poller.js';
import { resubscribeQuietly } from '../lib/pushclient.js';
import { heldLocks } from '../lib/locks.js';
import { RUNTIME_GEN } from '../lib/gen.js';
import {
  LOCAL, decideLaunch, afterStageFailure, commitFor, updateSummary, updateToast, liveUpdate,
  holdToRecover, coachDue, welcomeText, tellAgentPrefill, errorText, trialBanner, launchWatchdog, pollErrorAction,
} from '../lib/launch.js';
import { loadAccess, saveAccess, SAVE_ERROR } from '../lib/access.js';
import { CALENDAR_STATE, createCalendarConnection } from '../lib/calendarconnection.js';
import { createCalendarSignal } from '../lib/calendarsignal.js';
import { detectPlatform, captureInstallPrompt, editionFromHost } from '../lib/platform.js';
import { shadowCSS, adoptCSS } from '../ui/styles.js';
import { createHost, createFrame, setInsets } from '../shell/host.js';
import { createStore } from '../shell/store.js';
import { Shell } from '../shell/Shell.jsx';
import shellCSS from '../shell/shell.css';
import { createRuntimeAPI, installRuntimeAPI, watchBoot } from './runtime.js';
import { registerRuntimeSW } from './swclient.js';
import { Beacons, displayMode, visitorFor, keptVisitor, reportsAllowed, NOTICE_VERSION } from '../lib/beacon.js';
import { loadPrivacy, acknowledgePrivacy } from '../lib/privacy.js';

export const HUNG = 'It stopped before it finished starting.';
const SETTLE_MS = 5_000;
const HEARTBEAT_MS = 2_000;

/** Reads a build file: the staged cache first (works offline, with or without the worker), then the network. */
async function buildFile(url) {
  try {
    // Bounded: CacheStorage can be unusable (opaque origins, storage pressure).
    const hit = await Promise.race([globalThis.caches?.match?.(url), new Promise((r) => setTimeout(r, 2000))]);
    if (hit) return hit;
  } catch { /* no CacheStorage here */ }
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { code: 'missing' });
  return res;
}

/** Adds a stylesheet and waits for it (bounded), so the app's first paint is styled. */
function addStylesheet(doc, href) {
  return new Promise((resolve) => {
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    const done = () => { clearTimeout(t); resolve(); };
    const t = setTimeout(done, 3000);
    link.onload = done;
    link.onerror = done;
    doc.head.appendChild(link);
  });
}

function setIcon(doc, rel, href) {
  let link = doc.head.querySelector(`link[rel="${rel}"]`);
  if (!link) { link = doc.createElement('link'); link.rel = rel; doc.head.appendChild(link); }
  link.href = href;
}

/** The version name of a code, from what this device knows. */
async function nameOf(code, page) {
  if (page?.release?.version_code === code) return page.release.version_name;
  for (const r of page?.requests ?? []) if (r.test?.version_code === code) return r.test.version_name;
  if (page?.update?.version_code === code) return page.update.version_name;
  if (page?.release_delivery?.version_code === code) return page.release_delivery.version_name;
  const log = await getLocal(KEYS.log, []);
  return log.find((e) => e.code === code)?.version_name ?? '';
}

export async function boot(root = document.getElementById('app'), { doc = document, win = globalThis } = {}) {
  const platform = detectPlatform(env);
  const installPrompt = captureInstallPrompt(win);
  const store = createStore({
    edition: editionFromHost(env.host()), app: null, page: null, device: null, running: null, origin: env.origin(),
    screen: null, opening: null, access: null, panel: null, dialog: null, bannerCollapsed: false,
    write: { busy: false, error: null }, cancelState: null, toast: null, welcome: null, coachDue: false,
    pills: { refresh: false, switched: false, stale: false }, platform, canInstall: installPrompt.available(), connectCode: null,
    privacyDue: false, calendar: { ...CALENDAR_STATE },
  });
  const frame = createFrame(doc, root);
  const host = createHost(doc, `${shadowCSS}\n${shellCSS}`);
  const st = () => store.get();
  let poller = null;
  let outbox = null;
  let rawAccess = null;
  let settled = false;
  const launchId = uuid(); // RT3: this document's launch; it owns only its own boot marker
  let epochDigest = null;  // the selection this launch booted (its epoch, with launchId)
  let relaunches = 0;      // re-resolved launch plans after a refused claim
  let unowned = false;     // running a version another live window owns
  let revoked = false;     // E2E-4: the credential was refused during this session
  let beacons = null;      // launch reports (Amendment 25), once the app has started
  installPrompt.subscribe(() => store.set({ canInstall: installPrompt.available() }));

  // One signal for the panel's model and window.mytheseus.calendar.subscribe.
  const calendarSignal = createCalendarSignal();
  const calendar = createCalendarConnection({ onChange: (value) => store.set({ calendar: value }), navigate: env.navigate, signal: calendarSignal });
  const refreshCalendar = (force = false) => { if (!revoked && st().running != null) calendar.refresh({ force: force === true }); };
  // A return from consent or another tab can change the grant. Status probes
  // are read-only, bounded, and never request consent on their own.
  win.addEventListener?.('focus', refreshCalendar);
  doc.addEventListener?.('visibilitychange', () => { if (doc.visibilityState !== 'hidden') refreshCalendar(); });

  // ---- the controller: every tap in the shell lands here ----
  const ctl = {
    installPrompt,
    setInsets: (edges) => setInsets(doc, edges),
    openCoding: () => env.navigate('/_mt/coding'),
    reload: () => env.reload(),
    touch: () => settle(),
    openPanel: (panel) => { store.set({ panel }); if (panel === 'access') refreshCalendar(); },
    closePanel: () => store.set({ panel: null }),
    async loadPanel(name) {
      try {
        const mod = name === 'access' ? await import('../shell/access/AccessSettings.jsx') : await import('../shell/coach/Coach.jsx');
        adoptCSS(host.root, name === 'access' ? mod.accessCSS : mod.coachCSS);
        return mod;
      } catch {
        // §9.10: this window's generation is gone from the server.
        store.set((s) => ({ panel: null, pills: { ...s.pills, stale: true } }));
        return null;
      }
    },
    async saveAccess(settings) {
      const a = st().access;
      if (!a || a.saving) return;
      store.set({ access: { ...a, settings, saving: true, error: null } });
      const ok = await saveAccess(settings);
      store.set({ access: ok ? { ...a, settings, saving: false, error: null, loadFailed: false } : { ...a, saving: false, error: SAVE_ERROR } });
    },
    resetAccess: () => ctl.saveAccess({ ...st().access.defaults.settings }),
    refreshCalendar,
    connectCalendar: () => calendar.connect(),
    disconnectCalendar: () => calendar.disconnect(),
    setBannerCollapsed(collapsed) {
      store.set({ bannerCollapsed: collapsed });
      setLocal(KEYS.banner, { collapsed });
    },
    openDialog: (dialog) => store.set({ dialog }),
    closeDialog: () => store.set({ dialog: null }),
    async sendAction(request, action, text = '') {
      const { app, device } = st();
      let write;
      try {
        write = actionWrite(request, action, { text, installed: claimCode(device), pkg: app.application_id });
      } catch (e) {
        store.set({ dialog: null, write: { busy: false, error: { id: null, message: errorCopy(e) } } });
        return;
      }
      await send(() => ensureOutbox().enqueue(write), write.id, action);
    },
    async retryWrite() {
      const err = st().write.error;
      if (!err?.id) { store.set({ write: { busy: false, error: null } }); return; }
      await send(() => ensureOutbox().retry(err.id), err.id, err.action);
    },
    async dismissWrite() {
      const err = st().write.error;
      if (err?.id) await ensureOutbox().forget(err.id).catch(() => {});
      store.set({ write: { busy: false, error: null } });
    },
    async retrySafe() {
      await updateDevice((d) => withRetry(d));
      env.reload();
    },
    async tellAgent() {
      const d = await loadDevice();
      await setLocal(KEYS.prefill, tellAgentPrefill(d.failed_error));
      env.navigate('/_mt/coding');
    },
    connected: () => env.reload(),
    reconnect: () => env.navigate('/_mt/connect'),
    retryBoot: () => { store.set((s) => ({ screen: s.screen ? { ...s.screen, busy: true } : null })); launch(); },
    refreshSW: () => sw.refresh(),
    dismissToast: () => store.set({ toast: null }),
    dismissWelcome: () => store.set({ welcome: null }),
    async dismissCoach() {
      store.set({ coachDue: false });
      await setLocal(KEYS.coach, { at: new Date().toISOString() });
    },
    // The person acknowledged the launch-report notice: from now on this
    // origin may report, unless they turn reports off on /_mt/privacy. If
    // storage is unavailable the acknowledgement lasts this page only.
    async acceptPrivacy() {
      store.set({ privacyDue: false });
      // Not saved (storage failing): the acknowledgement lasts this page only.
      const p = await acknowledgePrivacy(env.now()).catch(() => ({ noticed_at: 'page', notice_version: NOTICE_VERSION, stopped: false }));
      beacons?.allow(reportsAllowed(p) && !revoked);
    },
    async privacyDetails() {
      settle();
      await acknowledgePrivacy(env.now()).catch(() => {});
      env.navigate('/_mt/privacy');
    },
    async install() {
      const r = await installPrompt.prompt();
      if (r === 'accepted') ctl.dismissCoach();
      else if (r === 'unavailable') store.set({ panel: 'coach' });
    },
  };

  render(<Shell store={store} ctl={ctl} />, host.root);

  const sw = registerRuntimeSW({
    container: env.serviceWorker(),
    onWaiting: () => store.set((s) => ({ pills: { ...s.pills, refresh: true } })),
    onReplaced: () => store.set((s) => ({ pills: { ...s.pills, switched: true } })),
    reload: () => env.reload(),
  });
  // Another window committed a switch: offer a reload, never hot-swap (R4). This
  // window hears its own announcements too, so it skips the codes it committed.
  const committed = new Set();
  const announce = (code) => { committed.add(code); announceSwitch(code); };
  onSwitch((code) => { if (!committed.has(code) && code !== st().running) store.set((s) => ({ pills: { ...s.pills, switched: true } })); });

  function ensureOutbox() {
    const { app } = st();
    if (!outbox) outbox = new Outbox({ scope: `${app.app_id}|${env.origin()}|${app.edition}` });
    return outbox;
  }

  // One write through the outbox; failures stay saved with Try again / Dismiss (never auto-retried).
  async function send(run, id, action) {
    store.set({ write: { busy: true, error: null } });
    try {
      const view = await run();
      store.set((s) => ({
        dialog: null,
        write: { busy: false, error: null },
        page: view && s.page ? { ...s.page, requests: s.page.requests.map((r) => (r.id === view.id ? view : r)) } : s.page,
        cancelState: action === 'cancel' ? (view?.phase === 'needs_owner' ? 'owner' : 'putting_back') : s.cancelState,
      }));
      startPoller();
      poller?.kick();
    } catch (e) {
      store.set({ dialog: null, write: { busy: false, error: { id, action, message: errorCopy(e) } } });
    }
  }

  async function settle() {
    if (settled) return;
    settled = true;
    await setLocal(LOCAL.launches, []);
  }

  // Nothing visible 12 s after load (a stuck promise anywhere) → a screen
  // with Try again, never a blank page.
  const watchdog = launchWatchdog(() => {
    store.set({ opening: null });
    showScreen({ kind: 'unavailable' });
  });

  function showScreen(screen) {
    watchdog.shown();
    frame.hidden = true;
    store.set({ screen, opening: null });
  }

  // ---- polling (trial, after Cancel, waiting for a first version) ----
  function startPoller() {
    if (poller || revoked) return;
    poller = new Poller({
      once: async ({ wait, signal }) => {
        const { app, device, page } = st();
        const next = await fetchState({ app, device, wait, cursor: page?.cursor, signal });
        if (next.unchanged) return;
        await onPage(next);
      },
      onOk: () => { if (st().pills.disconnected) store.set((s) => ({ pills: { ...s.pills, disconnected: false } })); },
      // RT5: a credential lost after a wait. The app keeps running; the pill offers to reconnect.
      // E2E-4: a 401 in a live session is this device being turned off. Stop
      // polling and queued work, and show the launch's revoked (or connect) screen.
      onError: async (e) => {
        if (pollErrorAction(e, { paired: true }) === 'retry') return;
        cutOff();
        await showUnauthorized();
      },
    });
    poller.start();
  }

  async function onPage(page) {
    const s = st();
    const cursorMoved = s.page && s.page.cursor !== page.cursor;
    store.set({ page });
    outbox?.confirm(page);
    // R16: access defaults and the name are live; re-read /v1/app when the page moves.
    if (cursorMoved) refreshApp();
    if (s.screen?.kind === 'waiting') {
      if (page.release_delivery) { poller?.stop(); poller = null; await act(decideLaunch({ device: s.device, app: s.app, page })); }
      return;
    }
    const mine = page.requests.find((r) => r.id === s.device?.selected?.request_id);
    if (s.cancelState && mine?.phase === 'needs_owner') store.set({ cancelState: 'owner' });
    // A running session keeps following (RT5); the decisions below only act
    // for a trial, Cancel or safe mode, and offer a reload otherwise.
    const live = liveUpdate({ device: s.device, app: s.app, page, cancelled: !!s.cancelState });
    if (live?.action === 'switch') await switchTo(live.envelope, page);
    else if (live?.action === 'offer' && s.running != null) store.set((x) => ({ pills: { ...x.pills, switched: true } }));
  }

  async function refreshApp() {
    try {
      const app = await fetchApp({ timeout: 10_000 });
      const raw = await getLocal(KEYS.info);
      const revision = raw?.access?.revision ?? '';
      store.set({ app });
      if (revision !== (rawAccess?.revision ?? '')) {
        rawAccess = raw?.access ?? null;
        store.set({ access: await loadAccess(app.edition, rawAccess) });
      }
    } catch { /* the next page tries again */ }
  }

  // After Cancel: the server's restore envelope (R2), staged, committed, then a fresh launch.
  async function switchTo(envelope, page) {
    poller?.stop();
    poller = null;
    try {
      const summary = await withBuildCacheUse(async () => {
        const staged = await stage(envelope);
        const summary = updateSummary({ update: envelope, page, edition: st().edition });
        await updateDevice((d) => withCommit(d, commitFor(envelope, staged.digest, summary)));
        return summary;
      });
      await logUpdate({ code: envelope.version_code, version_name: envelope.version_name, summary });
      announce(envelope.version_code);
      env.reload();
    } catch {
      store.set((s) => ({ pills: { ...s.pills, switched: true } }));
    }
  }

  // ---- launch ----
  async function launch() {
    let device;
    try {
      device = await loadDevice();
      // R1b: a launch that never reached ready() latches its code, unless the
      // document that set the marker is still alive (another window starting,
      // RT3): that one is not a crash.
      if (device.booting != null && !markerAlive(device, env.now(), await heldLocks())) {
        const seen = { booting: device.booting, boot_owner: device.boot_owner };
        device = await updateDevice((d) => withStaleMarker(d, seen, d.failed_error || HUNG));
      }
    } catch (e) {
      if (e?.code === 'storage') return showScreen({ kind: 'storage' });
      throw e;
    }
    store.set({ device, bannerCollapsed: (await getLocal(KEYS.banner, null))?.collapsed === true });

    let app = null;
    let unauthorized = false;
    let reachable = env.online();
    if (reachable) {
      try {
        app = await fetchApp({ timeout: 10_000 });
      } catch (e) {
        if (e?.code === 'unauthorized' || e?.status === 401) unauthorized = true;
        else reachable = false;
      }
    }
    if (unauthorized) return showUnauthorized();
    app = app ?? (await cachedApp());
    const raw = await getLocal(KEYS.info);
    if (!app) return showScreen({ kind: 'offline' });
    rawAccess = raw?.access ?? null;
    doc.title = app.name;
    store.set({ app, edition: app.edition, access: await loadAccess(app.edition, rawAccess) });

    let page = null;
    if (reachable) {
      try {
        page = await fetchState({ app, device, timeout: 10_000 });
      } catch (e) {
        if (e?.code === 'unauthorized' || e?.status === 401) return showUnauthorized();
      }
    }
    page = page ?? (await cachedPage());
    store.set({ page });
    return act(decideLaunch({ device, app, page }));
  }

  /** The credential is gone: no more polls, acknowledgements or push renewals from this page. */
  function cutOff() {
    revoked = true;
    calendar.stop();
    beacons?.allow(false);
    poller?.stop();
    poller = null;
    store.set({ running: null });
  }

  async function showUnauthorized() {
    const paired = await getLocal(KEYS.paired, false);
    const cached = await cachedApp();
    const edition = cached?.edition ?? editionFromHost(env.host());
    if (paired) return showScreen({ kind: 'revoked', owner: cached?.owner_name });
    return showScreen({ kind: 'connect', edition, appName: cached?.name, owner: cached?.owner_name });
  }

  async function act(decision) {
    const { app, page } = st();
    switch (decision.action) {
      case 'stage': {
        store.set({ opening: { progress: 0 }, screen: null });
        watchdog.shown(); // staging shows its own progress
        frame.hidden = false;
        const envelope = decision.envelope;
        let staged;
        let device;
        const summary = updateSummary({ update: envelope, page, edition: app.edition });
        try {
          await withBuildCacheUse(async () => {
            staged = await stage(envelope, { onProgress: (p) => store.set({ opening: { progress: p } }) });
            device = await updateDevice((d) => withCommit(d, commitFor(envelope, staged.digest, summary)));
          });
        } catch {
          if (staged) {
            // The new selection couldn't be saved, so it isn't committed.
            return st().device?.selected ? act({ action: 'run', code: st().device.selected.code }) : showScreen({ kind: 'storage' });
          }
          return act(afterStageFailure({ device: st().device, reason: decision.reason }));
        }
        await logUpdate({ code: envelope.version_code, version_name: envelope.version_name, summary });
        announce(envelope.version_code);
        store.set({ device, toast: updateToast({ update: envelope, page, edition: app.edition, reason: decision.reason }) });
        return run(envelope.version_code, staged.build, staged.digest);
      }
      case 'run': return run(decision.code, null, decision.digest ?? null);
      case 'safe': return safe(decision.code, decision.hung);
      case 'wait':
        showScreen({ kind: 'waiting' });
        startPoller();
        return undefined;
      case 'offline': return showScreen({ kind: 'offline' });
      default: return showScreen({ kind: 'unavailable' });
    }
  }

  async function safe(code) {
    const { page, app } = st();
    const device = await loadDevice();
    const id = device.selected?.code === code ? device.selected.request_id : null;
    const request = app.edition === 'coding'
      ? page?.requests.find((r) => (id ? r.id === id : r.test?.version_code === code) && r.test?.version_code === code) ?? null
      : null;
    store.set({ device });
    showScreen({ kind: 'safe', code, hung: device.failed_error === HUNG, error: device.failed_error, request });
    // A trial's safe mode waits for the server's restore after Put it back.
    if (request && app.edition === 'coding') startPoller();
  }

  // Run build `code` (R1b, §3.6): latch, styles, icons, window.mytheseus, import, mount, watch.
  async function run(code, build = null, digest = null) {
    const { app, page } = st();
    const base = `/_mt/b/${code}/`;
    // §9.9 hold-to-recover: only launches that run feature code count.
    const hold = holdToRecover(await getLocal(LOCAL.launches, []), env.now());
    await setLocal(LOCAL.launches, hold.list);
    if (hold.recover) { watchdog.shown(); env.replace('/_mt/coding'); return undefined; }
    let device;
    let claim;
    const locks = await heldLocks();
    try {
      // A conditional claim, in one transaction: still the selection, not
      // latched, and no live launch of this code owns the marker.
      device = await updateDevice((d) => {
        claim = claimBoot(d, { code, digest, launchId, alive: (x) => markerAlive(x, env.now(), locks) });
        return claim.device;
      });
    } catch {
      // RT2: without a durable boot marker a failure couldn't be caught safely.
      return showScreen({ kind: 'storage' });
    }
    if (!claim.claimed && claim.reason !== 'busy') {
      // The world moved on while this launch was deciding: decide again, from
      // scratch, and import nothing.
      if (++relaunches > 3) return showScreen({ kind: 'unavailable' });
      store.set({ device });
      return launch();
    }
    // 'busy': another live window is starting this same version. Run it too,
    // but this document owns nothing: its callbacks can't latch or clear.
    unowned = !claim.claimed;
    epochDigest = device.selected?.code === code ? device.selected.digest : null;
    // While starting, hold a Web Lock and keep a heartbeat, so another
    // document can tell this live launch from a crashed one (RT3).
    let booted = null;
    const bootDone = new Promise((r) => { booted = r; });
    try { globalThis.navigator?.locks?.request?.(bootLock(launchId), () => bootDone); } catch { /* no locks */ }
    const beat = setInterval(() => { updateDevice((d) => withHeartbeat(d, launchId)).catch(() => {}); }, HEARTBEAT_MS);
    const endBoot = () => { clearInterval(beat); booted?.(); };
    store.set({ device, running: code, screen: null });
    frame.hidden = false;
    try {
      build = build ?? (await (await buildFile(`${base}build.json`)).json());
      if (typeof build?.entry !== 'string') throw new Error('build.json has no entry');
    } catch {
      // Not the app's fault: the device can't read its own copy. Don't latch.
      endBoot();
      await updateDevice((d) => (d.boot_owner?.launch_id === launchId || !d.boot_owner ? { ...d, booting: null, boot_owner: null } : d)).catch(() => {});
      store.set({ running: null });
      return showScreen({ kind: 'unavailable' });
    }
    const versionName = await nameOf(code, page);
    await Promise.all((Array.isArray(build.css) ? build.css : []).map((c) => addStylesheet(doc, base + c)));
    setIcon(doc, 'icon', `${base}favicon.png`);
    setIcon(doc, 'apple-touch-icon', `${base}apple-touch-icon.png`);

    let failed = false;
    const watch = watchBoot({
      origin: env.origin(),
      onFail: async (text) => {
        failed = true;
        endBoot();
        if (unowned) {
          // Not ours to latch (the owning window decides); don't reload into it.
          store.set({ running: null });
          return safe(code);
        }
        try {
          await updateDevice((d) => withFailure(d, code, text, launchId, epochDigest));
        } catch {
          // RT2: the latch couldn't be saved. Reloading could loop, so stay:
          // hide the app and explain, with Try again as the only way on.
          store.set({ running: null });
          return showScreen({ kind: 'storage' });
        }
        store.set({ running: null });
        env.reload(); // Safe mode starts clean, with no feature code running (R2).
        return safe(code);
      },
    });
    installRuntimeAPI(createRuntimeAPI({
      info: { name: app.name, edition: app.edition, version_name: versionName, running_code: code },
      openCoding: () => { settle(); env.navigate('/_mt/coding'); },
      onReady: () => { if (!failed) { endBoot(); ready(code, watch); } },
      onCalendarUse: () => { if (!st().calendar.relevant) { calendar.use(); refreshCalendar(); } },
      onCalendarChange: () => refreshCalendar(true),
      calendarSignal,
    }), win);
    store.set({ opening: null });
    try {
      const mod = await env.importModule(new URL(base + build.entry, env.origin() || 'http://localhost').href);
      if (typeof mod?.mount !== 'function') throw new Error('The app has no mount(el) function.');
      await mod.mount(root);
      watchdog.shown();
    } catch (e) {
      watch.fail(errorText(e));
    }
  }

  async function ready(code, watch) {
    watchdog.shown();
    watch.stop();
    let out;
    try {
      out = await updateDevice((d) => withReady(d, code, RUNTIME_GEN, launchId, epochDigest));
    } catch {
      out = { device: await loadDevice().catch(() => st().device) };
    }
    let device = out.device;
    store.set({ device });
    // R15: acknowledge a newly started code right away; offline keeps it pending for the next launch.
    if (!revoked && device.pending_ack?.code === code) {
      try {
        await postJSON('/v1/app/installed', { code, runtime_gen: RUNTIME_GEN }, { timeout: 15_000 });
        const owner = device.device_id;
        device = await updateDevice((d) => withAckSent(d, code, owner));
        store.set({ device });
      } catch { /* sent on the next online launch */ }
    }
    pruneBuilds(async () => keptDigests(await loadDevice())).catch(() => {});
    setTimeout(settle, SETTLE_MS);
    const { app } = st();
    const layoutNow = st().access?.settings;
    const welcome = welcomeText(await getLocal(LOCAL.welcome, null), app.edition, layoutNow?.layout, layoutNow?.collapsed === true);
    if (welcome) await delLocal(LOCAL.welcome);
    const dismissed = await getLocal(KEYS.coach, null);
    store.set({ welcome, coachDue: coachDue({ platform, dismissed }) });
    // W5: if notifications were turned on here, quietly keep the subscription live.
    if (!revoked) resubscribeQuietly().catch(() => {});
    // RT5: every running session follows /state while visible, for the name,
    // access defaults, updates and a lost credential (not only trials).
    startPoller();
    refreshCalendar();
    startReports().catch(() => {});
  }

  // Launch reports (Amendment 25): one launch per page load, a resume after
  // 30 minutes out of sight, and how the app is shown. On the user edition
  // nothing leaves until the person has acknowledged the notice. The coding
  // edition is the owner's own and shows no notice; /_mt/privacy still turns
  // reports off there too. Turned off means no reports and no visitor id.
  async function startReports() {
    if (beacons || revoked) return;
    const { app } = st();
    const privacy = await loadPrivacy();
    if (privacy.stopped) return;
    // An open app this browser has no credential for is counted by a rotating
    // id, made only when a report can actually go.
    const anonymous = app.edition === 'user' && app.anonymous === true;
    // Re-evaluated on every report, so an id that reaches 30 days on a page
    // left open (or suspended) is replaced before it is sent again. Made in
    // one storage transaction, so tabs opened together agree on one id.
    let pageVisitor = null; // no durable storage: this page's own id
    const visitorId = async () => {
      if (!anonymous) return null;
      if (!kv.durable()) {
        const v = visitorFor(pageVisitor, env.now());
        if (v.save) pageVisitor = v.save;
        return v.id;
      }
      try {
        return await keptVisitor((fn) => kv.update(KEYS.privacy, fn), env.now());
      } catch {
        return null; // storage failing: this report goes without a visitor id
      }
    };
    beacons = new Beacons({
      post: (body) => postJSON('/v1/app/beacon', body, { timeout: 10_000 }),
      // Reports can be turned off on /_mt/privacy while this page stays open
      // (another tab, the back-forward cache): look again before each one.
      check: async () => !revoked && !(await loadPrivacy()).stopped,
      now: env.now,
      display: () => displayMode(win),
      standalone: () => win.navigator?.standalone === true,
      visitor: visitorId,
    });
    doc.addEventListener?.('visibilitychange', () => beacons.visibility(doc.visibilityState === 'hidden'));
    if (app.edition === 'coding' || reportsAllowed(privacy)) beacons.allow(true);
    else store.set({ privacyDue: true }); // OK or Details opens the gate
    beacons.launch();
  }

  await launch();
  // The shadow root is closed to the app; the screenshot harness gets it from here.
  return { store, ctl, shadow: host.root };
}
